import React, { useState, useEffect } from 'react';
import { 
  Building2, Mail, Phone, Globe, MapPin, Briefcase, User, Tag,
  Edit, Trash2, Plus, Save, X, Send, CheckSquare, MessageSquare,
  GitBranch, DollarSign, Calendar, ExternalLink, Clock, Paperclip, FileText,
  FolderOpen, Download, Link2, File, FileSpreadsheet, Image as ImageIcon
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent } from './ui/card';
import * as api from '../services/api';
import { formatDateTimePR } from '../utils/dateUtils';
import SocialMediaTab from './SocialMediaTab';

// Helper function to convert 24h time to 12h AM/PM format
const formatTimeAMPM = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Phone format helper
export const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `(${cleaned.slice(1,4)}) ${cleaned.slice(4,7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

// Phone input handler
export const handlePhoneInput = (value) => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 10) {
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0,3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
  }
  return value.slice(0, 14);
};

// Parse follow-up from note text
const parseFollowUp = (noteText) => {
  // Patterns to detect follow-up mentions with date/time
  const patterns = [
    /seguimiento[:\s]+(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})(?:\s+(?:a\s+las?\s+)?(\d{1,2}):?(\d{2})?\s*(am|pm)?)?/i,
    /llamar[:\s]+(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})(?:\s+(?:a\s+las?\s+)?(\d{1,2}):?(\d{2})?\s*(am|pm)?)?/i,
    /cita[:\s]+(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})(?:\s+(?:a\s+las?\s+)?(\d{1,2}):?(\d{2})?\s*(am|pm)?)?/i,
    /reunión[:\s]+(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})(?:\s+(?:a\s+las?\s+)?(\d{1,2}):?(\d{2})?\s*(am|pm)?)?/i,
    /pendiente[:\s]+(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})(?:\s+(?:a\s+las?\s+)?(\d{1,2}):?(\d{2})?\s*(am|pm)?)?/i,
  ];

  for (const pattern of patterns) {
    const match = noteText.match(pattern);
    if (match) {
      const day = parseInt(match[1]);
      const month = parseInt(match[2]) - 1;
      let year = parseInt(match[3]);
      if (year < 100) year += 2000;
      
      let hours = match[4] ? parseInt(match[4]) : 9;
      const minutes = match[5] ? parseInt(match[5]) : 0;
      const ampm = match[6]?.toLowerCase();
      
      if (ampm === 'pm' && hours < 12) hours += 12;
      if (ampm === 'am' && hours === 12) hours = 0;
      
      const date = new Date(year, month, day, hours, minutes);
      if (!isNaN(date.getTime())) {
        return {
          hasFollowUp: true,
          date: date.toISOString().split('T')[0],
          time: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
        };
      }
    }
  }
  
  return { hasFollowUp: false };
};

const EmpresaDetailModal = ({ 
  open, 
  onOpenChange, 
  empresaId, 
  currentUser, 
  onEmpresaUpdate,
  onEmpresaDelete,
  initialTab = 'info',
  canDelete = () => true
}) => {
  const [empresa, setEmpresa] = useState(null);
  const [users, setUsers] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [deals, setDeals] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Edit states
  const [isEditing, setIsEditing] = useState(false);
  const [editedEmpresa, setEditedEmpresa] = useState(null);
  
  // Contact edit states
  const [editingContactId, setEditingContactId] = useState(null);
  const [editedContact, setEditedContact] = useState(null);
  const [newContact, setNewContact] = useState({ name: '', email: '', phone: '', position: '', is_primary: false });
  
  // Contact notes states
  const [expandedContactId, setExpandedContactId] = useState(null);
  const [newContactNote, setNewContactNote] = useState('');
  
  // Note states
  const [newNote, setNewNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editedNoteContent, setEditedNoteContent] = useState('');
  
  // Task states
  const [newTask, setNewTask] = useState({ title: '', description: '', due_date: '', due_time: '', priority: 'Media' });
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editedTask, setEditedTask] = useState(null);
  
  // Task completion modal states
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completingTask, setCompletingTask] = useState(null);
  const [completionNote, setCompletionNote] = useState('');
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  const [followUpTask, setFollowUpTask] = useState({ title: '', date: '', time: '09:00', priority: 'Alta' });
  
  // Email states
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({ template_id: '', contact_id: '', subject: '', body: '' });
  const [emailAttachments, setEmailAttachments] = useState([]); // For user-added attachments
  const [templateAttachments, setTemplateAttachments] = useState([]); // From template
  const [selectedContact, setSelectedContact] = useState(null);
  const [emailRecipientType, setEmailRecipientType] = useState('empresa');
  
  // Add to pipeline states
  const [showAddToPipelineModal, setShowAddToPipelineModal] = useState(false);
  const [newDeal, setNewDeal] = useState({ pipeline_id: '', stage_id: '', value: 0, monthly_value: 0 });

  // Document states
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [newDocument, setNewDocument] = useState({
    name: '',
    document_type: 'other',
    url: '',
    description: '',
    google_doc_type: ''
  });
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (open && empresaId) {
      // Reset state immediately when empresaId changes to prevent showing stale data
      setEmpresa(null);
      setDeals([]);
      setIsEditing(false);
      setEditedEmpresa(null);
      setLoadError(null);
      loadData();
    } else if (!open) {
      // Clean up when modal closes
      setEmpresa(null);
      setDeals([]);
      setLoading(true);
      setLoadError(null);
      setIsEditing(false);
      setEditedEmpresa(null);
    }
  }, [open, empresaId]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  // Lazy load deals and pipelines only when deals tab is opened
  const [dealsLoaded, setDealsLoaded] = useState(false);
  useEffect(() => {
    if (activeTab === 'deals' && empresa && !dealsLoaded) {
      loadDealsAndPipelines();
    }
  }, [activeTab, empresa, dealsLoaded]);

  const loadDealsAndPipelines = async () => {
    try {
      const [pipelinesRes, dealsRes] = await Promise.all([
        api.getPipelines(),
        api.getDeals(null, empresaId)
      ]);
      setPipelines(pipelinesRes.data);
      setDeals(dealsRes.data);
      setDealsLoaded(true);
    } catch (error) {
      console.error('Error loading deals:', error);
    }
  };

  const loadData = async () => {
    // Capture the empresaId at the start of this request
    const requestedEmpresaId = empresaId;
    
    try {
      setLoading(true);
      setLoadError(null);
      setDealsLoaded(false);
      
      // Only load essential data first - empresa and users
      // Pipelines, deals, and templates are loaded lazily when needed
      const [empresaRes, usersRes] = await Promise.all([
        api.getEmpresa(requestedEmpresaId),
        api.getUsers()
      ]);
      
      // Only update state if the empresaId hasn't changed during the request
      // This prevents race conditions when clicking rapidly between empresas
      if (requestedEmpresaId === empresaId) {
        setEmpresa(empresaRes.data);
        setUsers(usersRes.data);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading empresa:', error);
      // Always set loading to false and show error
      setLoading(false);
      setLoadError(error.response?.data?.detail || error.message || 'Error al cargar empresa');
      setEmpresa(null);
    }
  };

  // Lazy load email templates when email tab is opened
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  useEffect(() => {
    if (activeTab === 'email' && !templatesLoaded) {
      loadTemplates();
    }
  }, [activeTab, templatesLoaded]);

  const loadTemplates = async () => {
    try {
      const templatesRes = await api.getEmailTemplates();
      setEmailTemplates(templatesRes.data);
      setTemplatesLoaded(true);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const handleSaveEmpresa = async () => {
    try {
      const response = await api.updateEmpresa(editedEmpresa.id, editedEmpresa);
      setEmpresa(response.data);
      setIsEditing(false);
      setEditedEmpresa(null);
      onEmpresaUpdate?.(response.data);
    } catch (error) {
      console.error('Error updating empresa:', error);
      alert('Error al actualizar empresa');
    }
  };

  const handleDeleteEmpresa = async () => {
    if (window.confirm('¿Está seguro de eliminar esta empresa y todos sus datos asociados?')) {
      try {
        await api.deleteEmpresa(empresa.id);
        onEmpresaDelete?.(empresa.id);
        onOpenChange(false);
      } catch (error) {
        console.error('Error deleting empresa:', error);
      }
    }
  };

  // Contact handlers
  const handleAddContact = async () => {
    if (!newContact.name) return;
    try {
      const contactData = { ...newContact, phone: newContact.phone };
      const response = await api.addContact(empresa.id, contactData);
      setEmpresa({ ...empresa, contacts: [...(empresa.contacts || []), response.data] });
      setNewContact({ name: '', email: '', phone: '', position: '', is_primary: false });
    } catch (error) {
      console.error('Error adding contact:', error);
    }
  };

  const handleUpdateContact = async () => {
    if (!editedContact) return;
    try {
      const response = await api.updateContact(editedContact.id, editedContact);
      setEmpresa({
        ...empresa,
        contacts: empresa.contacts.map(c => c.id === editedContact.id ? response.data : c)
      });
      setEditingContactId(null);
      setEditedContact(null);
    } catch (error) {
      console.error('Error updating contact:', error);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (window.confirm('¿Eliminar este contacto?')) {
      try {
        await api.deleteContact(contactId);
        setEmpresa({ ...empresa, contacts: empresa.contacts.filter(c => c.id !== contactId) });
      } catch (error) {
        console.error('Error deleting contact:', error);
      }
    }
  };

  // Contact Note handlers
  const handleAddContactNote = async (contactId) => {
    if (!newContactNote.trim()) return;
    try {
      const response = await api.addContactNote(contactId, { content: newContactNote }, currentUser?.name || 'Sistema');
      // Update contact's notes in local state
      setEmpresa({
        ...empresa,
        contacts: empresa.contacts.map(c => 
          c.id === contactId 
            ? { ...c, notes: [...(c.notes || []), response.data] }
            : c
        )
      });
      setNewContactNote('');
    } catch (error) {
      console.error('Error adding contact note:', error);
      alert('Error al agregar nota');
    }
  };

  const handleDeleteContactNote = async (contactId, noteId) => {
    try {
      await api.deleteContactNote(contactId, noteId);
      setEmpresa({
        ...empresa,
        contacts: empresa.contacts.map(c => 
          c.id === contactId 
            ? { ...c, notes: (c.notes || []).filter(n => n.id !== noteId) }
            : c
        )
      });
    } catch (error) {
      console.error('Error deleting contact note:', error);
    }
  };

  // Note handlers
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      const response = await api.addNote(empresa.id, { content: newNote }, currentUser?.name || 'Sistema');
      setEmpresa({ ...empresa, notes: [...(empresa.notes || []), response.data] });
      setNewNote('');
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await api.deleteNote(noteId);
      setEmpresa({ ...empresa, notes: empresa.notes.filter(n => n.id !== noteId) });
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditedNoteContent(note.content);
  };

  const handleSaveEditedNote = async (noteId) => {
    if (!editedNoteContent.trim()) return;
    try {
      const response = await api.updateNote(noteId, { content: editedNoteContent });
      setEmpresa({
        ...empresa,
        notes: empresa.notes.map(n => n.id === noteId ? { ...n, content: editedNoteContent } : n)
      });
      setEditingNoteId(null);
      setEditedNoteContent('');
    } catch (error) {
      console.error('Error updating note:', error);
    }
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditedNoteContent('');
  };

  // Task handlers
  const handleAddTask = async () => {
    if (!newTask.title) return;
    try {
      const taskData = {
        ...newTask,
        empresa_id: empresa.id,
        assigned_to: currentUser?.name || 'System',
        status: 'Pendiente'
      };
      const response = await api.createTask(taskData, currentUser?.email || 'system');
      setEmpresa({ ...empresa, tasks: [...(empresa.tasks || []), response.data] });
      setNewTask({ title: '', description: '', due_date: '', due_time: '', priority: 'Media' });
    } catch (error) {
      console.error('Error adding task:', error);
    }
  };

  const handleUpdateTask = async () => {
    if (!editedTask) return;
    try {
      const response = await api.updateTask(editedTask.id, editedTask);
      setEmpresa({
        ...empresa,
        tasks: empresa.tasks.map(t => t.id === editedTask.id ? response.data : t)
      });
      setEditingTaskId(null);
      setEditedTask(null);
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (window.confirm('¿Eliminar esta tarea?')) {
      try {
        await api.deleteTask(taskId);
        setEmpresa({ ...empresa, tasks: empresa.tasks.filter(t => t.id !== taskId) });
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
  };

  const handleToggleTask = (task) => {
    if (task.status !== 'Completada') {
      // Opening completion modal
      setCompletingTask(task);
      setCompletionNote('');
      setShowFollowUpForm(false);
      setFollowUpTask({ title: '', date: '', time: '09:00', priority: 'Alta' });
      setShowCompletionModal(true);
    } else {
      // Uncomplete the task
      handleUncompleteTask(task);
    }
  };

  const handleUncompleteTask = async (task) => {
    try {
      const response = await api.updateTask(task.id, { status: 'Pendiente' });
      setEmpresa({
        ...empresa,
        tasks: empresa.tasks.map(t => t.id === task.id ? response.data : t)
      });
    } catch (error) {
      console.error('Error uncompleting task:', error);
    }
  };

  const handleCompleteTaskWithNote = async (createFollowUp = false) => {
    if (!completingTask) return;
    
    try {
      // 1. Complete the task
      await api.updateTask(completingTask.id, { status: 'Completada' });
      
      // 2. Add the note
      if (completionNote.trim()) {
        const noteContent = `[Resultado de tarea "${completingTask.title}"]\n${completionNote}`;
        await api.addNote(empresa.id, { content: noteContent }, currentUser?.name || 'Sistema');
      }
      
      // 3. Create follow-up task if requested
      let newFollowUpTask = null;
      if (createFollowUp && followUpTask.title && followUpTask.date) {
        const followUpData = {
          title: followUpTask.title,
          description: `Seguimiento de la tarea completada.\n\nNota anterior:\n${completionNote}`,
          due_date: followUpTask.date,
          due_time: followUpTask.time,
          priority: followUpTask.priority,
          empresa_id: empresa.id,
          assigned_to: currentUser?.name || 'System',
          status: 'Pendiente'
        };
        const followUpResponse = await api.createTask(followUpData, currentUser?.email || 'system');
        newFollowUpTask = followUpResponse.data;
      }
      
      // 4. Reload empresa data
      const empresaRes = await api.getEmpresa(empresaId);
      setEmpresa(empresaRes.data);
      
      // 5. Close modal and show success
      setShowCompletionModal(false);
      setCompletingTask(null);
      setCompletionNote('');
      setShowFollowUpForm(false);
      
      if (newFollowUpTask) {
        alert(`Tarea completada y nueva tarea de seguimiento creada para ${followUpTask.date} a las ${followUpTask.time}`);
      }
    } catch (error) {
      console.error('Error completing task:', error);
      alert('Error al completar la tarea');
    }
  };

  const handleAddToGoogleCalendar = (task) => {
    const title = encodeURIComponent(task.title);
    const details = encodeURIComponent(task.description || `Tarea de ${empresa?.name}`);
    let dates = '';
    
    if (task.due_date) {
      const startDate = task.due_date.replace(/-/g, '');
      const startTime = task.due_time ? task.due_time.replace(':', '') + '00' : '090000';
      const endTime = task.due_time ? 
        (parseInt(task.due_time.split(':')[0]) + 1).toString().padStart(2, '0') + task.due_time.split(':')[1] + '00' : 
        '100000';
      dates = `${startDate}T${startTime}/${startDate}T${endTime}`;
    }
    
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${details}&dates=${dates}`;
    window.open(url, '_blank');
  };

  // Pipeline/Deal handlers
  const handleAddToPipeline = async () => {
    if (!newDeal.pipeline_id || !newDeal.stage_id) {
      alert('Seleccione un embudo y una etapa');
      return;
    }
    try {
      const pipeline = pipelines.find(p => p.id === newDeal.pipeline_id);
      const stage = pipeline?.stages?.find(s => s.id === newDeal.stage_id);
      
      const dealData = {
        empresa_id: empresa.id,
        empresa_name: empresa.name,
        empresa_category: empresa.category,
        pipeline_id: newDeal.pipeline_id,
        stage_id: newDeal.stage_id,
        stage_name: stage?.name || '',
        value: parseFloat(newDeal.value) || 0,
        monthly_value: parseFloat(newDeal.monthly_value) || 0,
        assigned_to: currentUser?.name || 'System'
      };
      
      const response = await api.createDeal(dealData);
      setDeals([...deals, response.data]);
      setShowAddToPipelineModal(false);
      setNewDeal({ pipeline_id: '', stage_id: '', value: 0, monthly_value: 0 });
    } catch (error) {
      console.error('Error adding to pipeline:', error);
      alert('Error al agregar al embudo');
    }
  };

  const handleRemoveFromPipeline = async (dealId) => {
    if (window.confirm('¿Eliminar de este embudo?')) {
      try {
        await api.deleteDeal(dealId);
        setDeals(deals.filter(d => d.id !== dealId));
      } catch (error) {
        console.error('Error removing from pipeline:', error);
      }
    }
  };

  // Document handlers
  const handleAddDocument = async () => {
    if (!newDocument.name) {
      alert('El nombre del documento es requerido');
      return;
    }
    
    setUploadingDocument(true);
    try {
      const response = await api.addEmpresaDocument(empresa.id, newDocument);
      setEmpresa(prev => ({
        ...prev,
        documents: [...(prev.documents || []), response.data.document]
      }));
      setShowAddDocumentModal(false);
      setNewDocument({ name: '', document_type: 'other', url: '', description: '', google_doc_type: '' });
    } catch (error) {
      console.error('Error adding document:', error);
      alert('Error al agregar documento');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Max 10MB
    if (file.size > 10 * 1024 * 1024) {
      alert('El archivo no puede superar 10MB');
      return;
    }
    
    setUploadingDocument(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result.split(',')[1];
        
        const docData = {
          name: file.name.replace(/\.[^/.]+$/, ''),
          document_type: newDocument.document_type || 'other',
          file_data: base64,
          file_name: file.name,
          mime_type: file.type,
          description: newDocument.description
        };
        
        try {
          const response = await api.addEmpresaDocument(empresa.id, docData);
          setEmpresa(prev => ({
            ...prev,
            documents: [...(prev.documents || []), response.data.document]
          }));
          setShowAddDocumentModal(false);
          setNewDocument({ name: '', document_type: 'other', url: '', description: '', google_doc_type: '' });
        } catch (err) {
          console.error('Error uploading:', err);
          alert('Error al subir archivo');
        }
        setUploadingDocument(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error reading file:', error);
      setUploadingDocument(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    
    try {
      await api.deleteEmpresaDocument(empresa.id, documentId);
      setEmpresa(prev => ({
        ...prev,
        documents: (prev.documents || []).filter(d => d.id !== documentId)
      }));
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Error al eliminar documento');
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await api.downloadEmpresaDocument(empresa.id, doc.id);
      if (response.data.type === 'redirect') {
        window.open(response.data.url, '_blank');
      } else if (doc.file_path) {
        // Download file from server
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/empresas/${empresa.id}/documents/${doc.id}/download`, '_blank');
      }
    } catch (error) {
      console.error('Error downloading:', error);
      // Fallback to URL if exists
      if (doc.url) window.open(doc.url, '_blank');
    }
  };

  const getDocumentIcon = (doc) => {
    if (doc.google_doc_type === 'sheet' || doc.mime_type?.includes('spreadsheet')) return FileSpreadsheet;
    if (doc.mime_type?.includes('image')) return ImageIcon;
    if (doc.url?.includes('docs.google.com')) return FileText;
    return File;
  };

  const getDocumentTypeLabel = (type) => {
    const types = {
      proposal: 'Propuesta',
      contract: 'Contrato',
      invoice: 'Factura',
      quote: 'Cotización',
      other: 'Otro'
    };
    return types[type] || type;
  };

  const getDocumentTypeColor = (type) => {
    const colors = {
      proposal: 'bg-purple-100 text-purple-700',
      contract: 'bg-blue-100 text-blue-700',
      invoice: 'bg-green-100 text-green-700',
      quote: 'bg-amber-100 text-amber-700',
      other: 'bg-gray-100 text-gray-700'
    };
    return colors[type] || colors.other;
  };

  // Email handlers
  const openEmailForContact = (contact) => {
    setSelectedContact(contact);
    setEmailRecipientType('contact');
    setEmailData({ template_id: '', contact_id: contact.id, subject: '', body: '' });
    setShowEmailModal(true);
  };

  const handleSelectTemplate = async (templateId) => {
    const template = emailTemplates.find(t => t.id === templateId);
    if (template) {
      let subject = template.subject;
      let body = template.body;
      const contact = selectedContact || empresa?.contacts?.[0];
      
      // Get assigned user info
      let assignedUser = null;
      if (empresa?.assigned_to) {
        try {
          const usersResponse = await api.getUsers();
          assignedUser = usersResponse.data.find(u => u.name === empresa.assigned_to);
        } catch (e) {
          console.error('Error getting assigned user:', e);
        }
      }
      
      // Replace tags
      const replacements = {
        '{{empresa_nombre}}': empresa?.name || '',
        '{{empresa_email}}': empresa?.email || '',
        '{{empresa_telefono}}': formatPhone(empresa?.phone) || '',
        '{{empresa_categoria}}': empresa?.category || '',
        '{{contacto_nombre}}': contact?.name || '',
        '{{contacto_email}}': contact?.email || '',
        '{{contacto_telefono}}': formatPhone(contact?.phone) || '',
        '{{contacto_posicion}}': contact?.position || '',
        '{{usuario_nombre}}': currentUser?.name || '',
        '{{usuario_email}}': currentUser?.email || '',
        '{{usuario_telefono}}': formatPhone(currentUser?.phone) || '',
        '{{usuario_posicion}}': currentUser?.position || '',
        '{{usuario_rol}}': currentUser?.role || '',
        '{{asignado_nombre}}': assignedUser?.name || empresa?.assigned_to || '',
        '{{asignado_email}}': assignedUser?.email || '',
        '{{asignado_telefono}}': formatPhone(assignedUser?.phone) || '',
        '{{asignado_posicion}}': assignedUser?.position || '',
        '{{fecha_actual}}': new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })
      };
      
      Object.entries(replacements).forEach(([tag, value]) => {
        subject = subject.replace(new RegExp(tag.replace(/[{}]/g, '\\$&'), 'g'), value);
        body = body.replace(new RegExp(tag.replace(/[{}]/g, '\\$&'), 'g'), value);
      });
      
      setEmailData({ ...emailData, template_id: templateId, subject, body });
      // Set template attachments if any
      setTemplateAttachments(template.attachments || []);
    }
  };

  const handleSendEmail = async () => {
    const contact = selectedContact || empresa?.contacts?.[0];
    const recipientEmail = emailRecipientType === 'contact' ? contact?.email : empresa?.email;
    
    if (!recipientEmail || !emailData.subject || !emailData.body) {
      alert('Faltan datos requeridos');
      return;
    }
    
    try {
      const emailPayload = {
        template_id: emailData.template_id || null,
        empresa_id: empresa.id,
        empresa_name: empresa.name,
        contact_id: contact?.id || null,
        contact_name: contact?.name || null,
        contact_email: recipientEmail,
        sender_id: currentUser?.id || 'system',
        sender_name: currentUser?.name || 'System',
        sender_email: currentUser?.email || 'system@infinito.com',
        subject: emailData.subject,
        body: emailData.body
      };
      
      // Combine manual attachments with template attachments
      const allAttachments = [...emailAttachments];
      const hasManualFiles = emailAttachments.length > 0;
      const hasTemplateAttachments = templateAttachments.length > 0;
      
      if (hasManualFiles || hasTemplateAttachments) {
        // Send template attachments as base64 data along with the email
        emailPayload.template_attachments = templateAttachments.map(att => ({
          filename: att.filename,
          file_data: att.file_data,
          content_type: att.content_type
        }));
        
        // Use the multipart endpoint with files
        await api.sendEmailWithAttachments(emailPayload, emailAttachments);
        const totalAttachments = emailAttachments.length + templateAttachments.length;
        alert(`Email enviado a ${recipientEmail} con ${totalAttachments} archivo(s) adjunto(s)`);
      } else {
        // Use the regular JSON endpoint
        await api.sendEmail(emailPayload);
        alert(`Email enviado a ${recipientEmail}`);
      }
      
      setShowEmailModal(false);
      setEmailData({ template_id: '', contact_id: '', subject: '', body: '' });
      setEmailAttachments([]);
      setTemplateAttachments([]);
      setSelectedContact(null);
    } catch (error) {
      console.error('Error sending email:', error);
      const errorMsg = error.response?.data?.detail || 'Error al enviar email';
      alert(errorMsg);
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      'Lead': 'bg-blue-100 text-blue-700',
      'Contactado': 'bg-cyan-100 text-cyan-700',
      'Propuesta': 'bg-yellow-100 text-yellow-700',
      'Negociación': 'bg-orange-100 text-orange-700',
      'Cerrado Ganado': 'bg-green-100 text-green-700',
      'Cerrado Perdido': 'bg-red-100 text-red-700'
    };
    return colors[stage] || 'bg-gray-100 text-gray-700';
  };

  const getCategoryBadge = (category) => {
    const badges = {
      'Empresa': 'bg-blue-100 text-blue-800',
      'Artista': 'bg-purple-100 text-purple-800',
      'Talento': 'bg-orange-100 text-orange-800'
    };
    return badges[category] || 'bg-gray-100 text-gray-800';
  };

  // Calculate total deal values
  const totalDealValue = deals.reduce((sum, d) => sum + (d.value || 0), 0);
  const totalMonthlyValue = deals.reduce((sum, d) => sum + (d.monthly_value || 0), 0);

  // Show loading state
  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4A843]"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Show error state
  if (loadError || !empresa) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl">
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="text-red-500 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Error al cargar empresa</h3>
            <p className="text-gray-500 mb-4">{loadError || 'No se pudo cargar la información'}</p>
            <Button variant="outline" onClick={() => loadData()}>
              Reintentar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center text-xl">
                <Building2 size={24} className="mr-2 text-[#D4A843]" />
                {empresa.name}
              </DialogTitle>
              <div className="flex items-center space-x-2">
                <Badge className={getStageColor(empresa.stage)}>{empresa.stage}</Badge>
                <Badge className={getCategoryBadge(empresa.category)}>{empresa.category}</Badge>
                {(empresa.email || empresa.contacts?.some(c => c.email)) && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => { setEmailRecipientType('empresa'); setSelectedContact(null); setShowEmailModal(true); }}
                    className="bg-[#D4A843]/10 border-[#D4A843] text-[#D4A843] hover:bg-[#D4A843] hover:text-white"
                  >
                    <Send size={16} className="mr-1" />
                    Email
                  </Button>
                )}
                {!isEditing && (
                  <Button variant="outline" size="sm" onClick={() => { setIsEditing(true); setEditedEmpresa({...empresa}); }}>
                    <Edit size={16} className="text-blue-500" />
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={handleDeleteEmpresa}>
                  <Trash2 size={16} className="text-red-500" />
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid grid-cols-7 w-full">
              <TabsTrigger value="info">Información</TabsTrigger>
              <TabsTrigger value="contacts">Contactos ({empresa.contacts?.length || 0})</TabsTrigger>
              <TabsTrigger value="notes">Notas ({empresa.notes?.length || 0})</TabsTrigger>
              <TabsTrigger value="tasks">Tareas ({empresa.tasks?.length || 0})</TabsTrigger>
              <TabsTrigger value="documents">Documentos ({empresa.documents?.length || 0})</TabsTrigger>
              <TabsTrigger value="pipelines">Embudos ({deals.length})</TabsTrigger>
              <TabsTrigger value="social">Social</TabsTrigger>
            </TabsList>
            
            <div className="flex-1 overflow-y-auto mt-4">
              {/* Info Tab */}
              <TabsContent value="info" className="space-y-4 m-0">
                {isEditing && editedEmpresa ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                        <Input value={editedEmpresa.name} onChange={(e) => setEditedEmpresa({ ...editedEmpresa, name: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                        <Select value={editedEmpresa.category || 'Empresa'} onValueChange={(v) => setEditedEmpresa({ ...editedEmpresa, category: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Empresa">Empresa</SelectItem>
                            <SelectItem value="Artista">Artista</SelectItem>
                            <SelectItem value="Talento">Talento</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email Principal</label>
                        <Input type="email" value={editedEmpresa.email || ''} onChange={(e) => setEditedEmpresa({ ...editedEmpresa, email: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                        <Input 
                          value={editedEmpresa.phone || ''} 
                          onChange={(e) => setEditedEmpresa({ ...editedEmpresa, phone: handlePhoneInput(e.target.value) })}
                          placeholder="(787) 555-1234"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                        <Input value={editedEmpresa.website || ''} onChange={(e) => setEditedEmpresa({ ...editedEmpresa, website: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                        <Input value={editedEmpresa.address || ''} onChange={(e) => setEditedEmpresa({ ...editedEmpresa, address: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Asignado a</label>
                        <Select value={editedEmpresa.assigned_to} onValueChange={(v) => setEditedEmpresa({ ...editedEmpresa, assigned_to: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {users.map(u => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex justify-end space-x-2 pt-4 border-t">
                      <Button variant="outline" onClick={() => { setIsEditing(false); setEditedEmpresa(null); }}>Cancelar</Button>
                      <Button onClick={handleSaveEmpresa} className="bg-[#D4A843] hover:bg-[#c49a3a]">
                        <Save size={16} className="mr-1" /> Guardar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    {empresa.email && (
                      <div className="flex items-center gap-2"><Mail size={16} className="text-gray-400" /><div><p className="text-xs text-gray-500">Email</p><p className="font-medium">{empresa.email}</p></div></div>
                    )}
                    {empresa.phone && (
                      <div className="flex items-center gap-2"><Phone size={16} className="text-gray-400" /><div><p className="text-xs text-gray-500">Teléfono</p><p className="font-medium">{formatPhone(empresa.phone)}</p></div></div>
                    )}
                    {empresa.website && (
                      <div className="flex items-center gap-2"><Globe size={16} className="text-gray-400" /><div><p className="text-xs text-gray-500">Website</p><a href={empresa.website} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-500 hover:underline">{empresa.website}</a></div></div>
                    )}
                    {empresa.address && (
                      <div className="flex items-center gap-2"><MapPin size={16} className="text-gray-400" /><div><p className="text-xs text-gray-500">Dirección</p><p className="font-medium">{empresa.address}</p></div></div>
                    )}
                    <div className="flex items-center gap-2"><User size={16} className="text-gray-400" /><div><p className="text-xs text-gray-500">Asignado a</p><p className="font-medium">{empresa.assigned_to}</p></div></div>
                    <div className="flex items-center gap-2"><Tag size={16} className="text-gray-400" /><div><p className="text-xs text-gray-500">Etapa</p><Badge className={getStageColor(empresa.stage)}>{empresa.stage}</Badge></div></div>
                  </div>
                )}
              </TabsContent>
              
              {/* Contacts Tab - Card View with Email button and Notes */}
              <TabsContent value="contacts" className="space-y-4 m-0">
                {/* Add Contact Form */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Agregar Contacto</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="Nombre *" value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} />
                    <Input placeholder="Posición" value={newContact.position} onChange={(e) => setNewContact({ ...newContact, position: e.target.value })} />
                    <Input placeholder="Email" type="email" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} />
                    <Input placeholder="Teléfono (787) 555-1234" value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: handlePhoneInput(e.target.value) })} />
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={newContact.is_primary} onChange={(e) => setNewContact({ ...newContact, is_primary: e.target.checked })} className="rounded" />
                      Contacto Principal
                    </label>
                    <Button onClick={handleAddContact} size="sm" className="bg-[#D4A843] hover:bg-[#c49a3a]">
                      <Plus size={16} className="mr-1" /> Agregar
                    </Button>
                  </div>
                </div>
                
                {/* Contacts Cards */}
                <div className="space-y-3">
                  {empresa.contacts?.map(contact => (
                    <Card key={contact.id} className="relative">
                      <CardContent className="p-4">
                        {editingContactId === contact.id ? (
                          <div className="space-y-3">
                            <Input value={editedContact.name} onChange={(e) => setEditedContact({ ...editedContact, name: e.target.value })} placeholder="Nombre" />
                            <Input value={editedContact.position || ''} onChange={(e) => setEditedContact({ ...editedContact, position: e.target.value })} placeholder="Posición" />
                            <Input value={editedContact.email || ''} onChange={(e) => setEditedContact({ ...editedContact, email: e.target.value })} placeholder="Email" type="email" />
                            <Input value={editedContact.phone || ''} onChange={(e) => setEditedContact({ ...editedContact, phone: handlePhoneInput(e.target.value) })} placeholder="Teléfono" />
                            <div className="flex justify-end gap-2">
                              <Button size="sm" variant="outline" onClick={() => { setEditingContactId(null); setEditedContact(null); }}><X size={14} /></Button>
                              <Button size="sm" onClick={handleUpdateContact} className="bg-[#D4A843] hover:bg-[#c49a3a]"><Save size={14} /></Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-gray-800">{contact.name}</h4>
                                  {contact.is_primary && <Badge className="bg-green-100 text-green-700 text-xs">Principal</Badge>}
                                </div>
                                {contact.position && <p className="text-sm text-gray-500">{contact.position}</p>}
                              </div>
                              <div className="flex gap-1">
                                {contact.email && (
                                  <button onClick={() => openEmailForContact(contact)} className="p-1 hover:bg-[#D4A843]/10 rounded" title="Enviar email">
                                    <Send size={14} className="text-[#D4A843]" />
                                  </button>
                                )}
                                <button 
                                  onClick={() => setExpandedContactId(expandedContactId === contact.id ? null : contact.id)} 
                                  className={`p-1 rounded ${expandedContactId === contact.id ? 'bg-blue-100' : 'hover:bg-blue-50'}`}
                                  title="Notas del contacto"
                                >
                                  <MessageSquare size={14} className={expandedContactId === contact.id ? 'text-blue-600' : 'text-blue-400'} />
                                  {contact.notes?.length > 0 && (
                                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
                                      {contact.notes.length}
                                    </span>
                                  )}
                                </button>
                                <button onClick={() => { setEditingContactId(contact.id); setEditedContact({...contact}); }} className="p-1 hover:bg-gray-100 rounded">
                                  <Edit size={14} className="text-gray-500" />
                                </button>
                                <button onClick={() => handleDeleteContact(contact.id)} className="p-1 hover:bg-red-50 rounded">
                                  <Trash2 size={14} className="text-red-500" />
                                </button>
                              </div>
                            </div>
                            <div className="mt-3 space-y-1 text-sm">
                              {contact.email && (
                                <div className="flex items-center gap-2 text-gray-600">
                                  <Mail size={14} className="text-gray-400" />
                                  <span>{contact.email}</span>
                                </div>
                              )}
                              {contact.phone && (
                                <div className="flex items-center gap-2 text-gray-600">
                                  <Phone size={14} className="text-gray-400" />
                                  <span>{formatPhone(contact.phone)}</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Contact Notes Section */}
                            {expandedContactId === contact.id && (
                              <div className="mt-4 pt-4 border-t border-gray-100">
                                <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                  <MessageSquare size={14} />
                                  Notas de {contact.name}
                                </h5>
                                <div className="flex gap-2 mb-3">
                                  <Input 
                                    placeholder="Agregar nota..." 
                                    value={newContactNote} 
                                    onChange={(e) => setNewContactNote(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleAddContactNote(contact.id)}
                                    className="flex-1 text-sm"
                                  />
                                  <Button 
                                    size="sm" 
                                    onClick={() => handleAddContactNote(contact.id)} 
                                    className="bg-blue-500 hover:bg-blue-600"
                                  >
                                    <Plus size={14} />
                                  </Button>
                                </div>
                                <div className="space-y-2 max-h-40 overflow-y-auto">
                                  {(contact.notes || []).map(note => (
                                    <div key={note.id} className="p-2 bg-blue-50 rounded text-sm">
                                      <div className="flex items-start justify-between">
                                        <p className="text-gray-700 whitespace-pre-wrap">{note.content}</p>
                                        <button onClick={() => handleDeleteContactNote(contact.id, note.id)} className="p-0.5 hover:bg-red-100 rounded">
                                          <Trash2 size={12} className="text-red-500" />
                                        </button>
                                      </div>
                                      <p className="text-xs text-gray-400 mt-1">{note.created_by} - {formatDateTimePR(note.created_at)}</p>
                                    </div>
                                  ))}
                                  {(!contact.notes || contact.notes.length === 0) && (
                                    <p className="text-xs text-gray-400 text-center py-2">No hay notas para este contacto</p>
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {(!empresa.contacts || empresa.contacts.length === 0) && (
                  <p className="text-gray-500 text-center py-4">No hay contactos añadidos</p>
                )}
              </TabsContent>
              
              {/* Notes Tab */}
              <TabsContent value="notes" className="space-y-4 m-0">
                <div className="flex space-x-2">
                  <Input placeholder="Escribe una nota..." value={newNote} onChange={(e) => setNewNote(e.target.value)} className="flex-1" onKeyPress={(e) => e.key === 'Enter' && handleAddNote()} />
                  <Button onClick={handleAddNote} className="bg-[#D4A843] hover:bg-[#c49a3a]"><Plus size={16} /></Button>
                </div>
                <div className="space-y-2">
                  {empresa.notes?.map(note => (
                    <div key={note.id} className="p-3 bg-gray-50 rounded-lg">
                      {editingNoteId === note.id ? (
                        <div className="space-y-2">
                          <textarea
                            value={editedNoteContent}
                            onChange={(e) => setEditedNoteContent(e.target.value)}
                            className="w-full p-2 border rounded-md text-sm"
                            rows={3}
                          />
                          <div className="flex justify-end space-x-2">
                            <Button variant="outline" size="sm" onClick={handleCancelEditNote}>Cancelar</Button>
                            <Button size="sm" onClick={() => handleSaveEditedNote(note.id)} className="bg-[#D4A843] hover:bg-[#c49a3a]">Guardar</Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between">
                            <p className="text-sm whitespace-pre-wrap flex-1">{note.content}</p>
                            <div className="flex items-center space-x-1 ml-2">
                              <Button variant="ghost" size="sm" onClick={() => handleEditNote(note)}><Edit size={14} className="text-blue-500" /></Button>
                              {canDelete('notes') && (
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteNote(note.id)}><Trash2 size={14} className="text-red-500" /></Button>
                              )}
                            </div>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">{note.created_by} - {formatDateTimePR(note.created_at)}</p>
                        </>
                      )}
                    </div>
                  ))}
                  {(!empresa.notes || empresa.notes.length === 0) && <p className="text-gray-500 text-center py-4">No hay notas añadidas</p>}
                </div>
              </TabsContent>
              
              {/* Tasks Tab - With Edit and Google Calendar */}
              <TabsContent value="tasks" className="space-y-4 m-0">
                {/* Add Task Form */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Agregar Tarea</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="Título de la tarea *" value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} className="col-span-2" />
                    <Input type="date" value={newTask.due_date} onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })} />
                    <Input type="time" value={newTask.due_time} onChange={(e) => setNewTask({ ...newTask, due_time: e.target.value })} />
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Alta">Alta</SelectItem>
                        <SelectItem value="Media">Media</SelectItem>
                        <SelectItem value="Baja">Baja</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={handleAddTask} size="sm" className="bg-[#D4A843] hover:bg-[#c49a3a]">
                      <Plus size={16} className="mr-1" /> Agregar Tarea
                    </Button>
                  </div>
                </div>
                
                {/* Tasks List */}
                <div className="space-y-2">
                  {empresa.tasks
                    ?.slice()
                    .sort((a, b) => {
                      // Sort by due_date and due_time (earliest first)
                      if (!a.due_date && !b.due_date) return 0;
                      if (!a.due_date) return 1;
                      if (!b.due_date) return -1;
                      const dateA = new Date(a.due_date + 'T' + (a.due_time || '00:00'));
                      const dateB = new Date(b.due_date + 'T' + (b.due_time || '00:00'));
                      return dateA - dateB;
                    })
                    .map(task => (
                    <Card key={task.id} className={task.status === 'Completada' ? 'opacity-60' : ''}>
                      <CardContent className="p-3">
                        {editingTaskId === task.id ? (
                          <div className="space-y-3">
                            <Input value={editedTask.title} onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })} placeholder="Título" />
                            <div className="grid grid-cols-2 gap-2">
                              <Input type="date" value={editedTask.due_date || ''} onChange={(e) => setEditedTask({ ...editedTask, due_date: e.target.value })} />
                              <Input type="time" value={editedTask.due_time || ''} onChange={(e) => setEditedTask({ ...editedTask, due_time: e.target.value })} />
                            </div>
                            <Select value={editedTask.priority} onValueChange={(v) => setEditedTask({ ...editedTask, priority: v })}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Alta">Alta</SelectItem>
                                <SelectItem value="Media">Media</SelectItem>
                                <SelectItem value="Baja">Baja</SelectItem>
                              </SelectContent>
                            </Select>
                            <div className="flex justify-end gap-2">
                              <Button size="sm" variant="outline" onClick={() => { setEditingTaskId(null); setEditedTask(null); }}><X size={14} /></Button>
                              <Button size="sm" onClick={handleUpdateTask} className="bg-[#D4A843] hover:bg-[#c49a3a]"><Save size={14} /></Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3 flex-1">
                              <button onClick={() => handleToggleTask(task)} className="flex-shrink-0">
                                <CheckSquare size={20} className={task.status === 'Completada' ? 'text-green-500' : 'text-gray-400 hover:text-[#D4A843]'} />
                              </button>
                              <div className="flex-1">
                                <span className={`font-medium ${task.status === 'Completada' ? 'line-through text-gray-500' : ''}`}>{task.title}</span>
                                <p className="text-xs text-gray-400">
                                  {task.due_date && `${task.due_date}`}
                                  {task.due_time && ` a las ${formatTimeAMPM(task.due_time)}`}
                                  {' • '}{task.assigned_to}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={task.priority === 'Alta' ? 'bg-red-100 text-red-700' : task.priority === 'Media' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}>
                                {task.priority}
                              </Badge>
                              <button onClick={() => handleAddToGoogleCalendar(task)} className="p-1 hover:bg-blue-50 rounded" title="Agregar a Google Calendar">
                                <Calendar size={14} className="text-blue-500" />
                              </button>
                              <button onClick={() => { setEditingTaskId(task.id); setEditedTask({...task}); }} className="p-1 hover:bg-gray-100 rounded">
                                <Edit size={14} className="text-gray-500" />
                              </button>
                              <button onClick={() => handleDeleteTask(task.id)} className="p-1 hover:bg-red-50 rounded">
                                <Trash2 size={14} className="text-red-500" />
                              </button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                  {(!empresa.tasks || empresa.tasks.length === 0) && <p className="text-gray-500 text-center py-4">No hay tareas asignadas</p>}
                </div>
              </TabsContent>
              
              {/* Pipelines/Embudos Tab - With Total Values */}
              <TabsContent value="pipelines" className="space-y-4 m-0">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">Embudos donde está esta empresa</h4>
                    {deals.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Total: <span className="font-semibold text-[#D4A843]">${totalDealValue.toLocaleString()}</span>
                        {totalMonthlyValue > 0 && <span className="text-green-600"> + ${totalMonthlyValue.toLocaleString()}/mes</span>}
                      </p>
                    )}
                  </div>
                  <Button onClick={() => setShowAddToPipelineModal(true)} size="sm" className="bg-[#D4A843] hover:bg-[#c49a3a]">
                    <Plus size={16} className="mr-1" /> Agregar a Embudo
                  </Button>
                </div>
                
                {deals.length > 0 ? (
                  <div className="space-y-3">
                    {deals.map(deal => {
                      const pipeline = pipelines.find(p => p.id === deal.pipeline_id);
                      return (
                        <Card key={deal.id}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <GitBranch size={20} className="text-[#D4A843]" />
                                <div>
                                  <h5 className="font-semibold text-gray-800">{pipeline?.name || 'Embudo'}</h5>
                                  <div className="flex items-center gap-2 mt-1">
                                    <Badge style={{ backgroundColor: deal.stage_color || '#6B7280', color: 'white' }}>
                                      {deal.stage_name}
                                    </Badge>
                                    {deal.is_won && <Badge className="bg-green-100 text-green-700">Ganado</Badge>}
                                    {deal.is_lost && <Badge className="bg-red-100 text-red-700">Perdido</Badge>}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <p className="text-lg font-bold text-[#D4A843]">${(deal.value || 0).toLocaleString()}</p>
                                  {deal.monthly_value > 0 && <p className="text-sm text-green-600">+${deal.monthly_value.toLocaleString()}/mes</p>}
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => handleRemoveFromPipeline(deal.id)}>
                                  <Trash2 size={14} className="text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <GitBranch size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Esta empresa no está en ningún embudo</p>
                    <p className="text-sm text-gray-400 mt-1">Agrega la empresa a un embudo para hacer seguimiento</p>
                  </div>
                )}
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents" className="space-y-4 m-0">
                {/* Add Document Button */}
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold text-gray-800">Documentos</h3>
                    <p className="text-sm text-gray-500">Propuestas, contratos, facturas y más</p>
                  </div>
                  <Button onClick={() => setShowAddDocumentModal(true)} className="bg-[#D4A843] hover:bg-[#c49a3a]">
                    <Plus size={16} className="mr-1" />
                    Agregar Documento
                  </Button>
                </div>

                {/* Documents List */}
                {empresa.documents?.length > 0 ? (
                  <div className="grid gap-3">
                    {empresa.documents.map((doc) => {
                      const DocIcon = getDocumentIcon(doc);
                      const isGoogleDoc = doc.url?.includes('docs.google.com') || doc.url?.includes('sheets.google.com') || doc.google_doc_id;
                      
                      return (
                        <Card key={doc.id} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3 flex-1">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  isGoogleDoc ? 'bg-blue-50' : 'bg-gray-100'
                                }`}>
                                  <DocIcon size={20} className={isGoogleDoc ? 'text-blue-600' : 'text-gray-600'} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-medium text-gray-800 truncate">{doc.name}</h4>
                                    <Badge className={getDocumentTypeColor(doc.document_type)}>
                                      {getDocumentTypeLabel(doc.document_type)}
                                    </Badge>
                                    {isGoogleDoc && (
                                      <Badge className="bg-blue-50 text-blue-600">
                                        <Link2 size={10} className="mr-1" />
                                        Google
                                      </Badge>
                                    )}
                                  </div>
                                  {doc.description && (
                                    <p className="text-sm text-gray-500 mt-1 line-clamp-1">{doc.description}</p>
                                  )}
                                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                                    <span>{new Date(doc.created_at).toLocaleDateString('es-ES')}</span>
                                    {doc.file_size && (
                                      <span>{(doc.file_size / 1024).toFixed(1)} KB</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 ml-2">
                                {(doc.url || doc.file_path || doc.google_doc_id) && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => handleDownloadDocument(doc)}
                                    title={isGoogleDoc ? "Abrir en Google" : "Descargar"}
                                  >
                                    {isGoogleDoc ? (
                                      <ExternalLink size={14} className="text-blue-600" />
                                    ) : (
                                      <Download size={14} className="text-gray-600" />
                                    )}
                                  </Button>
                                )}
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleDeleteDocument(doc.id)}
                                >
                                  <Trash2 size={14} className="text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FolderOpen size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No hay documentos adjuntos</p>
                    <p className="text-sm text-gray-400 mt-1">Agrega propuestas, contratos, facturas o cotizaciones</p>
                  </div>
                )}
              </TabsContent>

              {/* Social Media Tab */}
              <TabsContent value="social" className="space-y-4 m-0">
                <SocialMediaTab empresa={empresa} onUpdate={loadData} />
              </TabsContent>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Task Completion Modal */}
      <Dialog open={showCompletionModal} onOpenChange={setShowCompletionModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <CheckSquare size={20} className="mr-2 text-green-500" />
              Completar Tarea
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {completingTask && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{completingTask.title}</p>
                <p className="text-sm text-gray-500">Vence: {completingTask.due_date || 'Sin fecha'}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Resultado de la tarea (nota)
              </label>
              <textarea
                value={completionNote}
                onChange={(e) => setCompletionNote(e.target.value)}
                placeholder="Describe el resultado de esta tarea..."
                className="w-full h-32 p-3 border rounded-lg text-sm resize-none"
              />
            </div>
            
            {/* Follow-up Task Form */}
            {showFollowUpForm && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                <div className="flex items-center gap-2 text-blue-700 mb-2">
                  <Clock size={16} />
                  <span className="font-medium">Crear Tarea de Seguimiento</span>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Título</label>
                  <Input
                    value={followUpTask.title}
                    onChange={(e) => setFollowUpTask({...followUpTask, title: e.target.value})}
                    placeholder="Seguimiento..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Fecha</label>
                    <Input
                      type="date"
                      value={followUpTask.date}
                      onChange={(e) => setFollowUpTask({...followUpTask, date: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Hora</label>
                    <Input
                      type="time"
                      value={followUpTask.time}
                      onChange={(e) => setFollowUpTask({...followUpTask, time: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Prioridad</label>
                  <Select value={followUpTask.priority} onValueChange={(v) => setFollowUpTask({...followUpTask, priority: v})}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Alta">Alta</SelectItem>
                      <SelectItem value="Media">Media</SelectItem>
                      <SelectItem value="Baja">Baja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setShowCompletionModal(false); setCompletingTask(null); setShowFollowUpForm(false); }}>
              Cancelar
            </Button>
            <Button onClick={() => handleCompleteTaskWithNote(false)} className="bg-green-600 hover:bg-green-700">
              <Save size={16} className="mr-1" />
              Guardar Nota
            </Button>
            {!showFollowUpForm ? (
              <Button onClick={() => {
                setShowFollowUpForm(true);
                setFollowUpTask({
                  title: `Seguimiento: ${completingTask?.title || ''}`,
                  date: '',
                  time: '09:00',
                  priority: 'Alta'
                });
              }} className="bg-blue-600 hover:bg-blue-700">
                <Plus size={16} className="mr-1" />
                Crear Seguimiento
              </Button>
            ) : (
              <Button onClick={() => handleCompleteTaskWithNote(true)} className="bg-blue-600 hover:bg-blue-700">
                <Save size={16} className="mr-1" />
                Guardar y Crear Seguimiento
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to Pipeline Modal */}
      <Dialog open={showAddToPipelineModal} onOpenChange={setShowAddToPipelineModal}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Agregar a Embudo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Embudo *</label>
              <Select value={newDeal.pipeline_id} onValueChange={(v) => setNewDeal({ ...newDeal, pipeline_id: v, stage_id: '' })}>
                <SelectTrigger><SelectValue placeholder="Seleccionar embudo" /></SelectTrigger>
                <SelectContent>
                  {pipelines.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {newDeal.pipeline_id && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Etapa *</label>
                <Select value={newDeal.stage_id} onValueChange={(v) => setNewDeal({ ...newDeal, stage_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar etapa" /></SelectTrigger>
                  <SelectContent>
                    {pipelines.find(p => p.id === newDeal.pipeline_id)?.stages?.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor del Deal ($)</label>
                <Input type="number" value={newDeal.value} onChange={(e) => setNewDeal({ ...newDeal, value: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor Mensual ($)</label>
                <Input type="number" value={newDeal.monthly_value} onChange={(e) => setNewDeal({ ...newDeal, monthly_value: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddToPipelineModal(false)}>Cancelar</Button>
            <Button onClick={handleAddToPipeline} className="bg-[#D4A843] hover:bg-[#c49a3a]">Agregar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle><Send size={20} className="inline mr-2 text-[#D4A843]" />Enviar Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Enviar a</label>
                <Select value={emailRecipientType === 'contact' ? (selectedContact?.id || 'empresa') : 'empresa'} onValueChange={(v) => {
                  if (v === 'empresa') { setEmailRecipientType('empresa'); setSelectedContact(null); setEmailData({ ...emailData, contact_id: '' }); }
                  else { 
                    const c = empresa.contacts?.find(c => c.id === v); 
                    setEmailRecipientType('contact'); 
                    setSelectedContact(c); 
                    setEmailData({ ...emailData, contact_id: v }); 
                  }
                }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="empresa">Email de Empresa: {empresa?.email || 'N/A'}</SelectItem>
                    {empresa?.contacts?.filter(c => c.email).map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Usar Plantilla</label>
                <Select value={emailData.template_id || "none"} onValueChange={(v) => {
                  if (v === "none") {
                    setEmailData({ ...emailData, template_id: '', subject: '', body: '' });
                    setTemplateAttachments([]);
                  } else {
                    handleSelectTemplate(v);
                  }
                }}>
                  <SelectTrigger><SelectValue placeholder="Sin plantilla" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin plantilla</SelectItem>
                    {emailTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} {t.attachments?.length > 0 && `(${t.attachments.length} adjuntos)`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="p-2 bg-gray-50 rounded text-sm">
              <span className="text-gray-500">Para: </span>
              <span className="font-medium">{emailRecipientType === 'contact' ? selectedContact?.email : empresa?.email}</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Asunto *</label>
              <Input value={emailData.subject} onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contenido *</label>
              <textarea value={emailData.body} onChange={(e) => setEmailData({ ...emailData, body: e.target.value })} className="w-full h-40 p-3 border rounded-lg text-sm" />
            </div>
            
            {/* Attachments Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Paperclip size={14} className="inline mr-1" />
                Archivos Adjuntos
              </label>
              
              {/* Template attachments */}
              {templateAttachments.length > 0 && (
                <div className="mb-2">
                  <p className="text-xs text-gray-500 mb-1">De la plantilla:</p>
                  <div className="flex flex-wrap gap-2">
                    {templateAttachments.map((att, idx) => (
                      <div key={idx} className="flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-sm">
                        <FileText size={12} className="text-blue-500" />
                        <span className="truncate max-w-24">{att.filename}</span>
                        <button 
                          onClick={() => setTemplateAttachments(templateAttachments.filter((_, i) => i !== idx))}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* User attachments */}
              {emailAttachments.length > 0 && (
                <div className="mb-2">
                  <p className="text-xs text-gray-500 mb-1">Adicionales:</p>
                  <div className="flex flex-wrap gap-2">
                    {emailAttachments.map((att, idx) => (
                      <div key={idx} className="flex items-center gap-1 px-2 py-1 bg-gray-50 border rounded text-sm">
                        <FileText size={12} />
                        <span className="truncate max-w-24">{att.name}</span>
                        <button 
                          onClick={() => setEmailAttachments(emailAttachments.filter((_, i) => i !== idx))}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Add attachment button */}
              <div className="flex gap-2">
                <input 
                  type="file" 
                  id="email-attachment-input"
                  className="hidden"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    setEmailAttachments([...emailAttachments, ...files]);
                    e.target.value = '';
                  }}
                />
                <label 
                  htmlFor="email-attachment-input"
                  className="flex items-center gap-1 px-3 py-1.5 border rounded-lg text-sm cursor-pointer hover:bg-gray-50"
                >
                  <Paperclip size={14} />
                  Adjuntar Archivo
                </label>
                
                {/* Drive link option */}
                <button
                  onClick={() => {
                    const url = prompt('Ingresa el enlace de Google Drive:');
                    if (url) {
                      const linkText = `\n\n📎 Archivo adjunto: ${url}`;
                      setEmailData({ ...emailData, body: emailData.body + linkText });
                    }
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                >
                  <ExternalLink size={14} />
                  Enlace de Drive
                </button>
              </div>
              
              <p className="text-xs text-gray-400 mt-1">
                Los archivos adjuntos se enviarán directamente con el email vía Gmail.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)}>Cancelar</Button>
            <Button onClick={handleSendEmail} className="bg-[#D4A843] hover:bg-[#c49a3a]"><Send size={16} className="mr-1" /> Enviar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Document Modal */}
      <Dialog open={showAddDocumentModal} onOpenChange={setShowAddDocumentModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <FolderOpen size={20} className="mr-2 text-[#D4A843]" />
              Agregar Documento
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Documento *</label>
              <Input
                value={newDocument.name}
                onChange={(e) => setNewDocument({ ...newDocument, name: e.target.value })}
                placeholder="Propuesta Comercial 2024"
                data-testid="document-name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Documento</label>
              <Select 
                value={newDocument.document_type} 
                onValueChange={(v) => setNewDocument({ ...newDocument, document_type: v })}
              >
                <SelectTrigger data-testid="document-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="proposal">Propuesta</SelectItem>
                  <SelectItem value="contract">Contrato</SelectItem>
                  <SelectItem value="invoice">Factura</SelectItem>
                  <SelectItem value="quote">Cotización</SelectItem>
                  <SelectItem value="other">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción (opcional)</label>
              <Input
                value={newDocument.description}
                onChange={(e) => setNewDocument({ ...newDocument, description: e.target.value })}
                placeholder="Breve descripción del documento"
                data-testid="document-description-input"
              />
            </div>
            
            {/* URL or File Upload Options */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Opción 1:</span>
                <span className="text-gray-400">URL de Google Docs, Sheets, o enlace externo</span>
              </div>
              <Input
                value={newDocument.url}
                onChange={(e) => setNewDocument({ ...newDocument, url: e.target.value })}
                placeholder="https://docs.google.com/document/d/..."
                data-testid="document-url-input"
              />
              
              <div className="flex items-center gap-2 text-sm text-gray-600 pt-2 border-t">
                <span>Opción 2:</span>
                <span className="text-gray-400">Subir archivo (máx. 10MB)</span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-[#D4A843]/10 file:text-[#D4A843]
                  hover:file:bg-[#D4A843]/20
                  cursor-pointer"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg"
                data-testid="document-file-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDocumentModal(false)}>Cancelar</Button>
            <Button 
              onClick={handleAddDocument} 
              className="bg-[#D4A843] hover:bg-[#c49a3a]"
              disabled={uploadingDocument || !newDocument.name}
              data-testid="add-document-submit-btn"
            >
              {uploadingDocument ? (
                <><Clock size={16} className="mr-1 animate-spin" /> Subiendo...</>
              ) : (
                <><Plus size={16} className="mr-1" /> Agregar</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmpresaDetailModal;
